// apps/web/src/hooks/useAuth.ts
// Authentication hooks using TanStack Query

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { authApi, setAccessToken, type AuthUser, type LoginRequest, type ChangePasswordRequest } from '@/lib/api-client'

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  check: () => [...authKeys.all, 'check'] as const,
}

/**
 * Get current authenticated user
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async () => {
      const response = await authApi.me()
      return response.user
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  })
}

/**
 * Quick auth check (lightweight)
 */
export function useAuthCheck() {
  return useQuery({
    queryKey: authKeys.check(),
    queryFn: () => authApi.check(),
    staleTime: 60 * 1000, // 1 minute
    retry: false,
  })
}

/**
 * Login mutation
 */
export function useLogin() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (response) => {
      // Store access token for Bearer header fallback (cross-origin cookie issues)
      setAccessToken(response.accessToken)

      // Set user data in cache
      queryClient.setQueryData(authKeys.user(), response.user)
      queryClient.setQueryData(authKeys.check(), {
        authenticated: true,
        userId: response.user.id,
        role: response.user.role,
      })

      // Navigate based on mustChangePassword flag
      if (response.user.mustChangePassword) {
        navigate({ to: '/change-password' })
      } else {
        navigate({ to: '/' })
      }
    },
  })
}

/**
 * Logout mutation
 */
export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      // Clear token and all auth-related cache
      setAccessToken(null)
      queryClient.setQueryData(authKeys.user(), null)
      queryClient.setQueryData(authKeys.check(), { authenticated: false })
      queryClient.clear()

      // Navigate to login
      navigate({ to: '/login' })
    },
    onError: () => {
      // Still clear cache and redirect on error
      setAccessToken(null)
      queryClient.clear()
      navigate({ to: '/login' })
    },
  })
}

/**
 * Change password mutation
 */
export function useChangePassword() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => authApi.changePassword(data),
    onSuccess: () => {
      // Clear token and cache - user needs to re-login
      setAccessToken(null)
      queryClient.clear()
      navigate({ to: '/login' })
    },
  })
}

/**
 * Refresh token mutation
 */
export function useRefreshToken() {
  return useMutation({
    mutationFn: () => authApi.refresh(),
    onSuccess: (response) => {
      setAccessToken(response.accessToken)
    },
  })
}

/**
 * Reset user password (Super Admin only)
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: (userId: number) => authApi.resetPassword({ userId }),
  })
}

/**
 * Helper hook to check if user has specific role(s)
 */
export function useHasRole(roles: AuthUser['role'] | AuthUser['role'][]) {
  const { data: user } = useCurrentUser()

  if (!user) return false

  const roleArray = Array.isArray(roles) ? roles : [roles]
  return roleArray.includes(user.role)
}

/**
 * Helper hook to check if user has access to a specific branch
 */
export function useHasBranchAccess(branchId: number) {
  const { data: user } = useCurrentUser()

  if (!user) return false
  if (user.hasAllBranchAccess) return true

  return user.allowedBranchIds.includes(branchId)
}
